import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { PageHeader, Currency, Loader, Field } from "@/components/Atoms";
import { Coins, Dice5, Hand as HandIcon, BarChart3 } from "lucide-react";
import { PayoutFlash } from "@/components/PayoutFlash";
import { ChilletCoin } from "@/components/ChilletCoin";

export default function Gambling() {
  const [tab, setTab] = useState("coinflip");
  // Shared "big win" cinematic. Each sub-game calls `onWin(amount, label)`
  // after a successful payout; we render the floating skull burst here so
  // it sits above every game card and dismisses globally.
  const [flash, setFlash] = useState(null);
  const [defeatFlash, setDefeatFlash] = useState(null);
  const onWin = (amount, label = "BIG WIN") => setFlash({ amount, label });
  // Only show the muted defeat cinematic for "meaningful" losses (≥ 25
  // skulls) — every coinflip would be noise otherwise.
  const onLoss = (amount, label = "BUST") => {
    if (!amount || amount < 25) return;
    setDefeatFlash({ amount, label });
  };

  return (
    <div>
      <PageHeader title="The Gambling Den" subtitle="Risk your skulls. The dealer always wins." />
      <div className="flex gap-1 mb-6 border-b border-white/5 overflow-x-auto" data-testid="gamble-tabs">
        {[["coinflip", Coins, "Coinflip"], ["slots", Dice5, "Slots"], ["blackjack", HandIcon, "Blackjack"], ["stats", BarChart3, "Stats"]].map(([k, Icon, label]) => (
          <button key={k} onClick={() => setTab(k)}
            data-testid={`gamble-tab-${k}`}
            className={`flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-wider border-b-2 transition-colors whitespace-nowrap ${
              tab === k ? "border-[var(--teal)] text-[var(--teal)]" : "border-transparent text-[var(--muted)] hover:text-[var(--bone)]"
            }`}
          >
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>
      {tab === "coinflip" && <Coinflip onWin={onWin} onLoss={onLoss} />}
      {tab === "slots" && <Slots onWin={onWin} onLoss={onLoss} />}
      {tab === "blackjack" && <Blackjack onWin={onWin} onLoss={onLoss} />}
      {tab === "stats" && <Stats />}

      <PayoutFlash
        show={flash !== null}
        variant="skull"
        amount={flash?.amount || 0}
        label={flash?.label || "BIG WIN"}
        onDone={() => setFlash(null)}
      />
      <PayoutFlash
        show={defeatFlash !== null}
        variant="defeat"
        amount={defeatFlash?.amount || 0}
        label={defeatFlash?.label || "BUST"}
        duration={1400}
        onDone={() => setDefeatFlash(null)}
      />
    </div>
  );
}

function BetForm({ amount, setAmount, choice, children, busy, max, hint }) {
  return (
    <div className="space-y-3">
      <Field label="Bet Amount">
        <input
          className="input"
          type="number"
          min="10"
          {...(max ? { max } : {})}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          data-testid="bet-amount"
        />
        {hint && (
          <div className="text-[11px] text-[var(--muted)] mt-1" data-testid="bet-amount-hint">{hint}</div>
        )}
      </Field>
      {children}
    </div>
  );
}

function formatWait(seconds) {
  const s = Math.max(0, Math.floor(seconds));
  if (s >= 60) return `${Math.floor(s / 60)}m ${s % 60}s`;
  return `${s}s`;
}

function Coinflip({ onWin, onLoss }) {
  const [amount, setAmount] = useState(50);
  const [choice, setChoice] = useState("heads");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [flipping, setFlipping] = useState(false);
  // Captured during the flight so the coin actually lands on the API-
  // chosen side instead of always landing heads-up and then snapping.
  const [flightOutcome, setFlightOutcome] = useState(null);
  // Flip allowance (shared with the Discord bot's /gamble coinflip budget).
  const [limit, setLimit] = useState(null);
  const [secsLeft, setSecsLeft] = useState(0);
  const toast = useToast();
  const { user, refreshProfile } = useAuth();

  const loadLimit = async () => {
    try {
      const { data } = await api.get("/gamble/coinflip/limit");
      setLimit(data);
      setSecsLeft(data.retry_after || 0);
    } catch {
      // Non-fatal — the counter just doesn't render.
      setLimit(null);
    }
  };

  useEffect(() => { loadLimit(); }, []);

  useEffect(() => {
    if (secsLeft <= 0) return;
    const t = setInterval(() => {
      setSecsLeft((s) => {
        if (s <= 1) {
          loadLimit();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [secsLeft > 0]);

  const lockedOut = secsLeft > 0;
  // Coinflip-only bet ceiling (server-enforced; admin-tunable).
  const maxBet = Number(limit?.max_bet ?? 20000);
  const overMax = Number(amount) > maxBet;

  const flip = async () => {
    setBusy(true); setResult(null); setFlightOutcome(null);
    try {
      // Resolve the outcome BEFORE starting the visual flip so the GIF
      // (heads-landing or tails-landing) matches the API result.
      const { data } = await api.post(`/gamble/coinflip?choice=${choice}`, { amount: parseInt(amount, 10) });
      const d = data.details || {};
      setLimit((prev) => ({
        ...(prev || {}),
        remaining: d.flips_remaining ?? prev?.remaining,
        limit: d.flips_limit ?? prev?.limit,
        locked_until: d.locked_until ?? null,
      }));
      if (d.locked_until) {
        const secs = Math.max(0, Math.floor((new Date(d.locked_until) - Date.now()) / 1000));
        setSecsLeft(secs);
      }
      const outcome = data.details?.result === "tails" ? "tails" : "heads";
      setFlightOutcome(outcome);
      setFlipping(true);
      // GIFs are ~2s for heads (49 frames @ 50ms) / ~1.85s for tails;
      // use 2.5s so both finish before we reveal the result.
      setTimeout(() => {
        setResult(data);
        setFlipping(false);
        toast[data.won ? "success" : "error"](data.message);
        if (data.won) onWin?.(data.payout, "WIN");
        else onLoss?.(parseInt(amount, 10), "COINFLIP LOST");
        refreshProfile();
      }, 2500);
    } catch (e) {
      setFlipping(false);
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 429 && detail) {
        setSecsLeft(detail.retry_after || 0);
        setLimit((prev) => ({ ...(prev || {}), remaining: 0, limit: detail.limit ?? prev?.limit }));
        toast.error(detail.message || "Coinflip is on cooldown.");
      } else {
        toast.error(apiError(e));
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6 fade-up">
      <div className="glass-card p-7">
        <h3 className="text-xl mb-1">Coinflip</h3>
        <p className="text-xs text-[var(--muted)] mb-4">Pick heads or tails. 1:1 payout.</p>
        <BetForm
          amount={amount}
          setAmount={setAmount}
          max={maxBet}
          hint={`Max bet ${maxBet.toLocaleString()} coins`}
        >
          <div>
            <span className="label">Side</span>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setChoice("heads")} className={`btn ${choice === "heads" ? "" : "btn-secondary"}`} data-testid="choose-heads">Heads</button>
              <button onClick={() => setChoice("tails")} className={`btn ${choice === "tails" ? "" : "btn-secondary"}`} data-testid="choose-tails">Tails</button>
            </div>
          </div>
        </BetForm>
        <button onClick={flip} disabled={busy || flipping || lockedOut || !amount || overMax || (user?.points || 0) < amount} className="btn btn-lg w-full mt-4" data-testid="coinflip-flip-button">
          {lockedOut ? `Cooling down — ${formatWait(secsLeft)}` : overMax ? `Max bet is ${maxBet.toLocaleString()}` : busy || flipping ? "Spinning…" : "Flip"}
        </button>
        {limit != null && (
          <div className="text-center mt-3" data-testid="coinflip-limit-counter">
            <div className="text-xs text-[var(--muted)]">
              {lockedOut
                ? `All ${limit.limit} flips used — resets in ${formatWait(secsLeft)}`
                : `${limit.remaining} of ${limit.limit} flips left`}
            </div>
            <div className="text-[10px] text-[var(--muted)] opacity-70 mt-0.5">
              Shared with /gamble coinflip in Discord
            </div>
          </div>
        )}
      </div>
      <div className="glass-card p-7 flex items-center justify-center min-h-[320px]">
        <AnimatePresence mode="wait">
          {flipping ? (
            <ChilletCoin
              key={`flip-${flightOutcome}`}
              flipping
              outcome={flightOutcome || "heads"}
              data-testid="coinflip-coin"
            />
          ) : result ? (
            <motion.div key={result.message} initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-center" data-testid="coinflip-result">
              <ChilletCoin side={result.details?.result === "tails" ? "tails" : "heads"} />
              <div className={`${result.won ? "glow-gold" : "text-[var(--error)]"} text-2xl mt-4`}>{result.won ? "YOU WIN" : "YOU LOSE"}</div>
              <div className="text-sm text-[var(--muted)] mt-2">{result.message}</div>
              <div className="mt-3"><Currency value={result.new_balance} /> balance</div>
            </motion.div>
          ) : (
            <div className="text-center">
              <ChilletCoin side="idle" />
              <p className="text-[var(--muted)] text-sm mt-4">Place a bet to flip the Chillet coin.</p>
            </div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function Slots({ onWin, onLoss }) {
  const [amount, setAmount] = useState(100);
  const [result, setResult] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const { user, refreshProfile } = useAuth();

  const spin = async () => {
    setBusy(true); setSpinning(true);
    try {
      const { data } = await api.post("/gamble/slots", { amount: parseInt(amount, 10) });
      setTimeout(() => {
        setSpinning(false);
        setResult(data);
        toast[data.won ? "success" : "error"](data.message);
        if (data.won) {
          const mult = data.details?.multiplier || 1;
          // Jackpots (5x+) get a louder "JACKPOT" label; normal wins are "WIN"
          const label = mult >= 5 ? "JACKPOT" : "WIN";
          onWin?.(data.payout, label);
        } else {
          onLoss?.(parseInt(amount, 10), "SLOTS LOST");
        }
        refreshProfile();
      }, 1400);
    } catch (e) { setSpinning(false); toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const reels = result?.details?.reels || ["🍒", "🍋", "🍇"];

  return (
    <div className="grid lg:grid-cols-2 gap-6 fade-up">
      <div className="glass-card p-7">
        <h3 className="text-xl mb-1">Slots</h3>
        <p className="text-xs text-[var(--muted)] mb-4">Match 3 for a win. 💎=10x, 7=7x, ⭐=5x.</p>
        <BetForm amount={amount} setAmount={setAmount} />
        <button onClick={spin} disabled={busy || !amount || (user?.points || 0) < amount} className="btn btn-lg w-full mt-4" data-testid="slots-spin-button">
          {busy ? "Spinning…" : "Spin"}
        </button>
      </div>
      <div className="glass-card p-7 flex flex-col items-center justify-center min-h-[280px]">
        <div className="grid grid-cols-3 gap-3 mb-6" data-testid="slots-reels">
          {reels.map((r, i) => (
            <div key={i} className="w-20 h-20 sm:w-24 sm:h-24 bg-black/60 border border-[var(--teal-soft)] rounded flex items-center justify-center text-4xl overflow-hidden">
              {spinning ? <div className="reel-spin">{["🍒","🍋","🍊","🍇","⭐","💎","7️⃣"].map((e,j)=><div key={j}>{e}</div>)}</div> : r}
            </div>
          ))}
        </div>
        {result && !spinning && (
          <div className="text-center" data-testid="slots-result">
            <div className={result.won ? "glow-gold text-xl" : "text-[var(--error)] text-xl"}>{result.won ? `${result.details?.multiplier}x WIN` : "NO MATCH"}</div>
            <div className="text-xs text-[var(--muted)] mt-1">Balance: <Currency value={result.new_balance} /></div>
          </div>
        )}
      </div>
    </div>
  );
}

function Blackjack({ onWin, onLoss }) {
  const [amount, setAmount] = useState(100);
  const [session, setSession] = useState(null);
  const [final, setFinal] = useState(null);
  const [busy, setBusy] = useState(false);
  // Hand allowance + bet ceiling (server-enforced, admin-tunable) — same
  // shape as the coinflip limit so the UI reads identically.
  const [limit, setLimit] = useState(null);
  const [secsLeft, setSecsLeft] = useState(0);
  const toast = useToast();
  const { user, refreshProfile } = useAuth();

  const loadLimit = async () => {
    try {
      const { data } = await api.get("/gamble/blackjack/limit");
      setLimit(data);
      setSecsLeft(data.retry_after || 0);
    } catch {
      setLimit(null);
    }
  };

  useEffect(() => { loadLimit(); }, []);

  useEffect(() => {
    if (secsLeft <= 0) return;
    const t = setInterval(() => {
      setSecsLeft((s) => {
        if (s <= 1) { loadLimit(); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [secsLeft > 0]);

  const lockedOut = secsLeft > 0;
  const maxBet = Number(limit?.max_bet ?? 20000);
  const overMax = Number(amount) > maxBet;

  const applyLimitFromResult = (data) => {
    if (data?.hands_limit === undefined) return;
    setLimit((prev) => ({
      ...(prev || {}),
      remaining: data.hands_remaining ?? prev?.remaining,
      limit: data.hands_limit ?? prev?.limit,
      locked_until: data.locked_until ?? null,
    }));
    if (data.locked_until) {
      setSecsLeft(Math.max(0, Math.floor((new Date(data.locked_until) - Date.now()) / 1000)));
    }
  };

  const onError = (e) => {
    const detail = e?.response?.data?.detail;
    if (e?.response?.status === 429 && detail) {
      setSecsLeft(detail.retry_after || 0);
      setLimit((prev) => ({ ...(prev || {}), remaining: 0, limit: detail.limit ?? prev?.limit }));
      toast.error(detail.message || "Blackjack is on cooldown.");
    } else {
      toast.error(apiError(e));
    }
  };

  const start = async () => {
    setBusy(true); setFinal(null);
    try {
      const { data } = await api.post("/gamble/blackjack/start", { amount: parseInt(amount, 10) });
      if (data.dealer_total !== undefined) {
        setFinal(data); setSession(null);
        applyLimitFromResult(data);
        if (data.won) onWin?.(data.payout, "BLACKJACK");
        else onLoss?.(parseInt(amount, 10), "BLACKJACK BUST");
      } else { setSession(data); }
      refreshProfile();
    } catch (e) { onError(e); }
    finally { setBusy(false); }
  };

  const action = async (act) => {
    setBusy(true);
    try {
      const { data } = await api.post("/gamble/blackjack/action", { session_id: session.session_id, action: act });
      if (data.dealer_total !== undefined) {
        setFinal(data); setSession(null);
        applyLimitFromResult(data);
        toast[data.won ? "success" : "error"](data.message);
        if (data.won) onWin?.(data.payout, "WIN");
        else onLoss?.(parseInt(amount, 10), "BLACKJACK BUST");
      } else { setSession(data); }
      refreshProfile();
    } catch (e) { onError(e); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6 fade-up">
      <div className="glass-card p-7">
        <h3 className="text-xl mb-1">Blackjack</h3>
        <p className="text-xs text-[var(--muted)] mb-4">Beat the dealer to 21. Blackjack pays 3:2.</p>
        {!session && (
          <>
            <BetForm
              amount={amount}
              setAmount={setAmount}
              max={maxBet}
              hint={`Max bet ${maxBet.toLocaleString()} coins`}
            />
            <button
              onClick={start}
              disabled={busy || lockedOut || overMax || !amount || (user?.points || 0) < amount}
              className="btn btn-lg w-full mt-4"
              data-testid="bj-start-button"
            >
              {lockedOut
                ? `Cooling down — ${formatWait(secsLeft)}`
                : overMax
                  ? `Max bet is ${maxBet.toLocaleString()}`
                  : busy ? "Dealing…" : "Deal"}
            </button>
            {limit != null && (
              <div className="text-center mt-3" data-testid="bj-limit-counter">
                <div className="text-xs text-[var(--muted)]">
                  {lockedOut
                    ? `All ${limit.limit} hands used — resets in ${formatWait(secsLeft)}`
                    : `${limit.remaining} of ${limit.limit} hands left`}
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <div className="glass-card p-7 min-h-[300px]">
        {session && (
          <div className="space-y-4" data-testid="bj-session">
            <div>
              <div className="label">Dealer shows</div>
              <Hand cards={[session.dealer_showing, "?"]} />
            </div>
            <div>
              <div className="label">Your hand ({session.player_total})</div>
              <Hand cards={session.player_hand} />
            </div>
            <div className="flex gap-2 flex-wrap">
              {session.actions?.includes("hit") && <button onClick={() => action("hit")} disabled={busy} className="btn flex-1" data-testid="bj-hit">Hit</button>}
              {session.actions?.includes("stand") && <button onClick={() => action("stand")} disabled={busy} className="btn btn-secondary flex-1" data-testid="bj-stand">Stand</button>}
              {session.actions?.includes("double") && <button onClick={() => action("double")} disabled={busy} className="btn btn-warning flex-1" data-testid="bj-double">Double</button>}
            </div>
          </div>
        )}
        {final && (
          <div className="text-center" data-testid="bj-final">
            <div className={final.won ? "glow-gold text-2xl" : "text-[var(--error)] text-2xl"}>{final.won ? "WIN" : "LOSE"}</div>
            <div className="text-sm text-[var(--muted)] mt-1">{final.message}</div>
            <div className="mt-4">
              <div className="label">Dealer ({final.dealer_total})</div>
              <Hand cards={final.dealer_hand} />
            </div>
            <div className="mt-3">
              <div className="label">You ({final.player_total})</div>
              <Hand cards={final.player_hand} />
            </div>
            <div className="mt-3"><Currency value={final.new_balance} /> balance</div>
          </div>
        )}
        {!session && !final && <p className="text-[var(--muted)] text-sm">Bet to deal.</p>}
      </div>
    </div>
  );
}

function Hand({ cards }) {
  return (
    <div className="flex gap-2 flex-wrap">
      {cards.map((c, i) => (
        <div key={i} className="w-12 h-16 bg-[var(--bone)] text-[var(--bg-deep)] rounded flex items-center justify-center font-semibold border border-white/20">
          {c}
        </div>
      ))}
    </div>
  );
}

function Stats() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/gamble/stats").then(({ data }) => setStats(data)).finally(() => setLoading(false));
  }, []);
  if (loading) return <Loader />;
  if (!stats || stats.message) return <p className="text-[var(--muted)] text-center py-8">No gambling history yet.</p>;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 fade-up">
      <Stat label="Total Bets" value={stats.total_bets} />
      <Stat label="Wins" value={stats.wins} />
      <Stat label="Losses" value={stats.losses} />
      <Stat label="Win Rate" value={`${stats.win_rate_pct ?? 0}%`} />
      <Stat label="Wagered" value={(stats.total_wagered || 0).toLocaleString()} />
      <Stat label="Won" value={(stats.total_won || 0).toLocaleString()} />
      <Stat label="Lost" value={(stats.total_lost || 0).toLocaleString()} />
      <Stat label="Biggest Win" value={(stats.biggest_win || 0).toLocaleString()} highlight />
    </div>
  );
}
function Stat({ label, value, highlight }) {
  return (
    <div className="glass-card p-5">
      <div className="label">{label}</div>
      <div className={`text-2xl mt-1 ${highlight ? "glow-gold" : "glow-teal"}`}>{value}</div>
    </div>
  );
}
